import type { ComputedRef } from 'vue'
import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { DisciplineProgressReport } from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useListsStore } from '@shared/model'
import { resolveUrl } from '@shared/lib/url'
import { useDayjs } from '@shared/lib/format'
import { useAdminRoutes } from '@shared/lib/routes'
import { AppRoute } from '@shared/config'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'
import { ROWS_PER_PAGE_OPTIONS } from '@shared/ui/paginations'

import { getDisciplineName } from './discipline-general-info'
import { useDisciplinePrivileges } from './discipline-privileges.hook'
import { useDisciplineStore } from './store'
import { useDisciplineProgressReportsStore } from './progress-reports/store'

interface TableDisciplineProgressReportsReturn {
  reports: ComputedRef<DisciplineProgressReport[]>
  isLoading: ComputedRef<boolean>
  hasReports: ComputedRef<boolean>
  meta: ComputedRef<Meta>
  perPageOptions: ComputedRef<number[]>
  disciplineName: ComputedRef<string>
  disciplineUrl: ComputedRef<string>
  canManageProgressReport: ComputedRef<boolean>
  getDueDate: (report: DisciplineProgressReport) => string
  getTutorName: (report: DisciplineProgressReport) => string | null
  getTutorUrl: (report: DisciplineProgressReport) => string | null
  isExpanded: (report: DisciplineProgressReport) => boolean
  getDetailsRowId: (report: DisciplineProgressReport) => string
  toggleDetails: (report: DisciplineProgressReport) => void
  openEdit: (report: DisciplineProgressReport) => void
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
}

const EMPTY_CELL = '-'

export const useTableDisciplineProgressReports = (): TableDisciplineProgressReportsReturn => {
  const router = useRouter()
  const route = useRoute()
  const dayjs = useDayjs()
  const store = useDisciplineProgressReportsStore()
  const disciplineStore = useDisciplineStore()
  const listsStore = useListsStore()
  const { canManageProgressReport } = useDisciplinePrivileges()

  const expandedReportId = ref<number | null>(null)

  const reports = computed<DisciplineProgressReport[]>(() => store.getReports)
  const isLoading = computed<boolean>(() => store.isLoading)
  const hasReports = computed<boolean>(() => store.hasReports)
  const meta = computed<Meta>(() => store.getMeta ?? DEFAULT_APP_TABLE_META)

  const perPageOptions = computed<number[]>(() => {
    const current = meta.value.per_page
    if (!current || ROWS_PER_PAGE_OPTIONS.includes(current)) return ROWS_PER_PAGE_OPTIONS

    return [...ROWS_PER_PAGE_OPTIONS, current].sort((a, b) => a - b)
  })

  /** Every row is this discipline's own, so the column names the page's discipline. */
  const disciplineName = computed<string>(() =>
    getDisciplineName(disciplineStore.getDiscipline?.code ?? '', listsStore.getLists?.discipline.code))
  const disciplineUrl = computed<string>(() =>
    resolveUrl(AppRoute.DisciplinesGeneralInfo, { id: route.params.id as string }) ?? '')

  const getDueDate = (report: DisciplineProgressReport): string => {
    const dueDate = report.tutor_task?.due_date
    return dueDate ? dayjs(dueDate).format(DateFormat.KanbanDate) : EMPTY_CELL
  }

  const getTutorName = (report: DisciplineProgressReport): string | null => {
    const tutor = report.tutor_task?.tutor
    if (!tutor) return null

    return tutor.full_name || `${tutor.first_name ?? ''} ${tutor.last_name ?? ''}`.trim() || null
  }

  /** Tutors are not migrated yet: the legacy tutor page. */
  const getTutorUrl = (report: DisciplineProgressReport): string | null => {
    const tutorId = report.tutor_task?.tutor?.id
    return tutorId ? useAdminRoutes('admin.tutor.show', { tutor_id: tutorId }) : null
  }

  const isExpanded = (report: DisciplineProgressReport): boolean => expandedReportId.value === report.id

  const getDetailsRowId = (report: DisciplineProgressReport): string => `progress-report-details-${report.id}`

  const toggleDetails = (report: DisciplineProgressReport): void => {
    expandedReportId.value = isExpanded(report) ? null : report.id
  }

  const openEdit = (report: DisciplineProgressReport): void => {
    store.setEditingReport(report)
  }

  const setChangePage = async (value: number): Promise<void> => {
    store.setCurrentPage(value)
    await router.push({
      query: {
        ...route.query,
        page: String(value),
      },
    })
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    store.setPerPage(value)
    store.setCurrentPage(1)
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
        page: undefined,
      },
    })
  }

  watch(reports, () => {
    expandedReportId.value = null
  })

  return {
    reports,
    isLoading,
    hasReports,
    meta,
    perPageOptions,
    disciplineName,
    disciplineUrl,
    canManageProgressReport,
    getDueDate,
    getTutorName,
    getTutorUrl,
    isExpanded,
    getDetailsRowId,
    toggleDetails,
    openEdit,
    setChangePage,
    setChangePerPage,
  }
}
